import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import { Box, Button, FlexColumn, FlexRow, FlexRowCenter, GridRow, MenuItem, MoreHorizMenu, OverflowText, Text, TextField } from "designSystem/index";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { FC } from "react";
import { CustomViewDialogProps } from "./CustomViewDialog";
import css from "./CustomViewDialogHeader.module.css";
import { useCustomViewDialogStore } from "./CustomViewDialogStore";
import CustomViewFavoriteButton from "./CustomViewFavoriteButton";
import DeleteButton from "./DeleteButton";
import { ShareViewWith } from "./options/CustomViewOptions";
import { SaveButton } from "designSystem/components/button/ValidationButton";
import CustomViewDialogHeaderForSingleCustomView from "./CustomViewDialogHeaderForSingleCustomView";

export const PermissionIconMap = {
	[ShareViewWith.EVERYONE]: <PublicIcon />,
	[ShareViewWith.ME]: <PersonIcon />,
	[ShareViewWith.ROLES]: <PeopleIcon />,
};

const CustomViewDialogHeader: FC<CustomViewDialogProps> = observer((props) => {
	const { onCancel, onSaveAndClose } = props;
	const {
		customViewStore: {
			discardDuplicatedCustomView,
			saveAsCurrent,
			saveButtonDisabled,
			saveCurrentWithRefresh,
			saveNCloseButtonDisabled,
			selectedCustomViewItem,
			showDiscard,
			showSave,
			showSaveAndClose,
			showSaveAsAsPrimary,
			showSaveAsInMore,
		},
		settingStore: {
			isEditingActiveDescription,
			isEditingActiveName,
			onBlurDescription,
			onBlurName,
			prefDescription,
			prefName,
			setActiveDescription,
			setActiveName,
			setIsEditingActiveDescription,
			setIsEditingActiveName,
			shareWith,
		},
		showActionMenuButton,
	} = useCustomViewDialogStore();

	const {
		addToFavorite,
		hideDeleteButton,
		onOpenMenu,
		removeFromFavorites,
		showAddToFavorite,
		showRemoveFromFavorite,
	} = selectedCustomViewItem || {}; // the intermediate state could be null, must add {}

	const setIsEditingActiveDescriptionTrue = () => setIsEditingActiveDescription(true);

	return (
		<FlexRowCenter
			borderBottom={STANDARD_BORDER_STYLE}
			data-csi-automation="CustomViewDialogHeader"
			gap={2}
			px={6}
			py={3}
		>
			<FlexColumn
				bgcolor={grey[300]}
				borderRadius="4px"
				justifyContent="center"
				p={2}
			>
				{PermissionIconMap[shareWith]}
			</FlexColumn>
			<FlexColumn
				growNoOverflow
			>
				<FlexRow>
					<>
						{isEditingActiveName ? (
							<TextField
								autoFocus
								borderless
								defaultValue={prefName}
								onBlur={onBlurName}
								onChange={setActiveName}
								placeholderRes="CustomViewEnterName"
								selectOnFocus
								sx={{ fontSize: 16 }}
							/>
						) : (
							<OverflowText
								onClick={() => setIsEditingActiveName(true)}
								size={16}
								text={prefName}
								variant="h4"
							/>
						)
						}
					</>
					<Box marginBottom={1}>
						{selectedCustomViewItem &&
							<CustomViewFavoriteButton className={css.favoriteButton} customViewItem={selectedCustomViewItem} />
						}
					</Box>
				</FlexRow>
				<FlexRowCenter>
					{isEditingActiveDescription ? (
						<TextField
							autoFocus
							borderless
							defaultValue={prefDescription}
							onBlur={onBlurDescription}
							onChange={setActiveDescription}
							placeholderRes="CustomViewEnterDescription"
							selectOnFocus
						/>
					) : prefDescription ? (
						<OverflowText
							fontWeight={400}
							grow
							onClick={setIsEditingActiveDescriptionTrue}
							size={10}
							text={prefDescription}
							variant="h1"
						/>
					) : (
						<Text
							onClick={setIsEditingActiveDescriptionTrue}
							res="CustomViewEnterDescription"
							style={{ color: "var(--Grey700)" }}
						/>
					)}
				</FlexRowCenter>
			</FlexColumn>
			<FlexColumn flexShrink="0">
				<GridRow spacing={2}>
					<Button
						design="link"
						onClick={onCancel}
						res="Cancel"
					/>
					{showSave && (
						<SaveButton
							design="secondary"
							disabled={saveButtonDisabled}
							onAction={saveCurrentWithRefresh}
						/>
					)}
					{showSaveAndClose && (
						<SaveButton
							disabled={saveNCloseButtonDisabled}
							onAction={onSaveAndClose}
							res="Save_Close"
						/>
					)}
					{showSaveAsAsPrimary && (
						<Button
							design="primary"
							onClick={saveAsCurrent}
							res="SaveAs"
						/>
					)}
					{showActionMenuButton && (
						<MoreHorizMenu
							onOpen={onOpenMenu}
						>
							{showAddToFavorite && (
								<MenuItem
									onClick={addToFavorite}
									res="CustomViewFavoritesAdd"
								/>
							)}
							{showRemoveFromFavorite && (
								<MenuItem
									onClick={removeFromFavorites}
									res="CustomViewFavoritesRemove"
								/>
							)}
							{showDiscard && (
								<MenuItem
									onClick={discardDuplicatedCustomView}
									res="BUTTON_DISCARD"
								/>
							)}
							{showSaveAsInMore && (
								<MenuItem
									onClick={saveAsCurrent}
									res="SaveAs"
								/>
							)}
							{selectedCustomViewItem && !hideDeleteButton && (
								<DeleteButton customViewItem={selectedCustomViewItem} />
							)}
						</MoreHorizMenu>
					)}
				</GridRow>
			</FlexColumn>
		</FlexRowCenter>
	);
});

export default CustomViewDialogHeader;